import { useEffect, useMemo, useRef, useState } from "react";
import { restoreSession, signOut as clearAuthSession, withActiveOrganization } from "./auth";
import {
  createRemoteCommitment,
  createRemoteReport,
  createRemoteVisit,
  getRemoteConversation,
  getRemoteUserContext,
  importRemoteConversation,
  listRemoteConversations,
  listRemoteReports,
  listRemoteVisits,
  requestRemoteConversationAnalysis,
  sendRemoteWhatsAppText,
  updateRemoteCommitment,
  updateRemoteConversation,
} from "./api";
import { getRemoteMessageMedia } from "./mediaApi";
import { startRemoteMediaReview } from "./mediaReviewApi";
import { startRemoteAudioTranscription } from "./transcriptionApi";
import { AuthModal } from "./components/AuthModal";
import { OrganizationsView, UsersView } from "./components/AdminViews";
import { ChannelsView } from "./components/ChannelsView";
import { CustomersView } from "./components/CustomersView";
import { ConversationView } from "./components/ConversationView";
import { HomeView } from "./components/HomeView";
import { ImportStudio } from "./components/ImportStudio";
import { PilotCenter } from "./components/PilotCenter";
import { PaseFirmeNewWhatsAppModal } from "./components/PaseFirmeNewWhatsAppModal";
import { CommitmentsView, ReportsView, VisitsView } from "./components/WorkflowViews";
import { Icon, type IconName } from "./icons";
import { saveInternalNote } from "./internalNotes";
import { callRemoteVsmart, importRemoteConversationHistory } from "./integrationApi";
import {
  countUnreadConversations,
  conversationsNeedingDetail,
  excludeDemoConversations,
  isDemoConversation,
  mergeRemoteConversations,
} from "./conversationSync";
import {
  loadLocalConversations,
  loadLocalReports,
  loadLocalVisits,
  saveLocalConversations,
  saveLocalReports,
  saveLocalVisits,
} from "./localStore";
import { conversations as demoConversations } from "./mockData";
import { createReportFromConversation, createVisitFromConversation } from "./report";
import type {
  AuthSession,
  Commitment,
  Conversation,
  ConversationReport,
  MediaDownload,
  Message,
  UserContext,
  Visit,
  WorkspaceView,
} from "./types";

const DEMO_IDS = new Set(demoConversations.map((item) => item.id));

const VIEW_TITLE: Record<WorkspaceView, { kicker: string; title: string }> = {
  home: { kicker: "GRUPO PGV", title: "Inicio" },
  pilot: { kicker: "ACTIVACIÓN", title: "Centro de piloto" },
  conversations: { kicker: "OPERACIÓN", title: "Conversaciones" },
  commitments: { kicker: "SEGUIMIENTO", title: "Compromisos" },
  visits: { kicker: "TERRENO", title: "Visitas" },
  reports: { kicker: "TRAZABILIDAD", title: "Reportes" },
  import: { kicker: "ACTIVACIÓN", title: "Importar chat" },
  customers: { kicker: "DIRECTORIO", title: "Clientes" },
  channels: { kicker: "CANALES", title: "WhatsApp" },
  organizations: { kicker: "ADMINISTRACIÓN", title: "Organizaciones" },
  users: { kicker: "ADMINISTRACIÓN", title: "Usuarios" },
};

export function App() {
  const [view, setView] = useState<WorkspaceView>("home");
  const [session, setSession] = useState<AuthSession | null>(null);
  const [userContext, setUserContext] = useState<UserContext | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [reports, setReports] = useState<ConversationReport[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [booting, setBooting] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [sendingConversationId, setSendingConversationId] = useState("");
  const [openingMediaId, setOpeningMediaId] = useState("");
  const [reviewingMediaId, setReviewingMediaId] = useState("");
  const [transcribingMessageId, setTranscribingMessageId] = useState("");
  const [analyzingConversationId, setAnalyzingConversationId] = useState("");
  const [authOpen, setAuthOpen] = useState(false);
  const [newWhatsAppOpen, setNewWhatsAppOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toast, setToast] = useState<{ tone: "success" | "warning" | "danger"; text: string } | null>(null);
  const [remoteSync, setRemoteSync] = useState<{
    status: "idle" | "syncing" | "ok" | "error";
    lastSuccessAt: string;
    error: string;
  }>({ status: "idle", lastSuccessAt: "", error: "" });
  const conversationsRef = useRef<Conversation[]>([]);

  useEffect(() => {
    let active = true;
    void restoreSession()
      .then(async (restored) => {
        if (!active) return;
        if (restored) await connectSession(restored, active);
        else hydrateLocal();
      })
      .catch((cause) => {
        if (!active) return;
        hydrateLocal();
        notify("warning", "No se pudo recuperar la sesión: " + errorMessage(cause));
      })
      .finally(() => active && setBooting(false));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (booting || session) return;
    saveLocalConversations(conversations.filter((item) => !DEMO_IDS.has(item.id)));
  }, [booting, conversations, session]);

  useEffect(() => {
    if (booting || session) return;
    saveLocalVisits(visits);
  }, [booting, session, visits]);

  useEffect(() => {
    if (booting || session) return;
    saveLocalReports(reports);
  }, [booting, reports, session]);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 4_200);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    if (!session || !userContext?.activeOrganizationId) return;
    let active = true;
    let running = false;

    const refresh = async () => {
      if (!active || running || document.visibilityState === "hidden") return;
      running = true;
      try {
        await refreshRemoteWorkspace(session, () => active);
      } finally {
        running = false;
      }
    };

    const interval = window.setInterval(() => void refresh(), 12_000);
    const onFocus = () => void refresh();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void refresh();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [session, userContext?.activeOrganizationId]);

  const pendingCommitments = useMemo(
    () => conversations.flatMap((item) => item.commitments).filter((item) => item.status === "pending").length,
    [conversations],
  );
  const unreadConversationCount = useMemo(
    () => countUnreadConversations(conversations),
    [conversations],
  );

  async function connectSession(
    baseSession: AuthSession,
    active = true,
    preferredOrganizationId?: string,
  ) {
    const context = await getRemoteUserContext(baseSession, preferredOrganizationId);
    const organizationId = context.activeOrganizationId ?? context.organizations[0]?.id;
    const scopedSession = organizationId
      ? withActiveOrganization(baseSession, organizationId)
      : baseSession;
    if (!active) return;
    setSession(scopedSession);
    setUserContext({ ...context, activeOrganizationId: organizationId });
    if (organizationId) await hydrateRemote(scopedSession, active);
    else {
      setConversations([]);
      setVisits([]);
      setReports([]);
      setSelectedId("");
    }
  }

  async function hydrateRemote(activeSession: AuthSession, active = true) {
    setRemoteSync((current) => ({ ...current, status: "syncing", error: "" }));
    try {
      const [remoteBase, remoteVisits, remoteReports] = await Promise.all([
        listRemoteConversations(activeSession),
        listRemoteVisits(activeSession),
        listRemoteReports(activeSession),
      ]);
      const hiddenDemoConversationIds = new Set(
        remoteBase.filter(isDemoConversation).map((conversation) => conversation.id),
      );
      const base = excludeDemoConversations(remoteBase);
      const detailResults = await Promise.allSettled(
        base.slice(0, 50).map((conversation) => getRemoteConversation(conversation.id, activeSession)),
      );
      const details = new Map(
        detailResults
          .filter((result): result is PromiseFulfilledResult<Conversation> => result.status === "fulfilled")
          .map((result) => [result.value.id, result.value]),
      );
      if (!active) return;
      const items = base.map((item) => details.get(item.id) ?? item);
      setConversations(items);
      setVisits(remoteVisits.filter((visit) => !hiddenDemoConversationIds.has(visit.conversationId)));
      setReports(remoteReports.filter((report) => !hiddenDemoConversationIds.has(report.conversationId)));
      setSelectedId((current) => current && items.some((item) => item.id === current)
        ? current
        : items[0]?.id ?? "");
      setRemoteSync({ status: "ok", lastSuccessAt: new Date().toISOString(), error: "" });
    } catch (cause) {
      if (!active) return;
      const message = errorMessage(cause);
      setConversations([]);
      setVisits([]);
      setReports([]);
      setSelectedId("");
      setRemoteSync({ status: "error", lastSuccessAt: "", error: message });
      notify("danger", `No se pudo abrir el espacio AWS. No se cargaron datos de demostración: ${message}`);
    }
  }

  async function refreshRemoteWorkspace(
    activeSession: AuthSession,
    isActive: () => boolean = () => true,
    showFeedback = false,
  ) {
    setRemoteSync((current) => ({ ...current, status: "syncing", error: "" }));
    try {
      const base = excludeDemoConversations(await listRemoteConversations(activeSession));
      const current = conversationsRef.current;
      const candidates = conversationsNeedingDetail(base, current);
      const detailResults = await Promise.allSettled(
        candidates.map((conversation) => getRemoteConversation(conversation.id, activeSession)),
      );
      const details = new Map(
        detailResults
          .filter((result): result is PromiseFulfilledResult<Conversation> => result.status === "fulfilled")
          .map((result) => [result.value.id, result.value]),
      );
      if (!isActive()) return;
      const items = mergeRemoteConversations(base, current, details);
      setConversations(items);
      setSelectedId((selected) => selected && items.some((item) => item.id === selected)
        ? selected
        : items[0]?.id ?? "");
      setRemoteSync({ status: "ok", lastSuccessAt: new Date().toISOString(), error: "" });
      if (showFeedback) notify("success", "Conversaciones actualizadas desde AWS.");
    } catch (cause) {
      if (!isActive()) return;
      const message = errorMessage(cause);
      setRemoteSync((current) => ({ ...current, status: "error", error: message }));
      if (showFeedback) notify("warning", `No se pudo actualizar la bandeja: ${message}`);
    }
  }

  async function manualRefresh() {
    if (!session) return;
    await refreshRemoteWorkspace(session, () => true, true);
  }

  function hydrateLocal() {
    const local = loadLocalConversations();
    const unique = new Map<string, Conversation>();
    for (const item of [...local, ...demoConversations]) unique.set(item.id, item);
    const items = [...unique.values()];
    setConversations(items);
    setVisits(loadLocalVisits());
    setReports(loadLocalReports());
    setSelectedId((current) => current || items[0]?.id || "");
  }

  async function selectConversation(id: string) {
    setSelectedId(id);
    setView("conversations");
    setMobileMenuOpen(false);
    if (!session) return;
    setLoadingDetail(true);
    try {
      const detail = await getRemoteConversation(id, session);
      setConversations((items) => items.map((item) => (item.id === id ? detail : item)));
    } catch (cause) {
      notify("warning", `No se pudo actualizar la evidencia: ${errorMessage(cause)}`);
    } finally {
      setLoadingDetail(false);
    }
  }

  async function handleImport(conversation: Conversation) {
    if (session) {
      if (isDemoConversation(conversation)) {
        throw new Error("Los casos ficticios sólo se crean en el modo demostración local.");
      }
      await importRemoteConversation(conversation, session);
      const saved = await getRemoteConversation(conversation.id, session).catch(() => conversation);
      setConversations((items) => [saved, ...items.filter((item) => item.id !== saved.id)]);
      notify("success", "Conversación importada y auditada en AWS.");
    } else {
      setConversations((items) => [conversation, ...items.filter((item) => item.id !== conversation.id)]);
      notify("success", "Conversación agregada a la demostración de este navegador.");
    }
    setSelectedId(conversation.id);
    setView("conversations");
  }

  async function handleVsmartQuery(
  conversation: Conversation,
  action: Parameters<typeof callRemoteVsmart>[0],
  args: Record<string, unknown> = {},
) {
  if (!session) throw new Error("VSMART_REQUIRES_AWS_SESSION");
  return callRemoteVsmart(action, args, session, { conversationId: conversation.id });
}

  async function handleHistoryImport(
    target: Conversation,
    imported: Conversation,
    fileName: string,
  ) {
    if (!session) throw new Error("HISTORY_IMPORT_REQUIRES_AWS_SESSION");
    const result = await importRemoteConversationHistory(
      target.id,
      imported,
      fileName,
      session,
    );
    const saved = await getRemoteConversation(target.id, session);
    setConversations((items) => items.map((item) => item.id === target.id ? saved : item));
    setSelectedId(target.id);
    setView("conversations");
    notify(
      "success",
      `Historial fusionado: ${result.insertedMessages} mensajes nuevos y ${result.duplicateMessages} duplicados omitidos.`,
    );
  }

  async function toggleCommitment(conversationId: string, commitment: Commitment) {
    const nextStatus = commitment.status === "done" ? "pending" : "done";
    const confirmsSuggestion = commitment.source === "ai" && commitment.reviewStatus === "suggested";
    setConversations((items) =>
      items.map((conversation) =>
        conversation.id === conversationId
          ? {
              ...conversation,
              commitments: conversation.commitments.map((item) =>
                item.id === commitment.id
                  ? { ...item, status: nextStatus, ...(confirmsSuggestion ? { reviewStatus: "confirmed" as const } : {}) }
                  : item,
              ),
            }
          : conversation,
      ),
    );
    if (!session) return;
    try {
      await updateRemoteCommitment(commitment.id, {
        status: nextStatus,
        ...(confirmsSuggestion ? { reviewStatus: "confirmed" as const } : {}),
      }, session);
    } catch (cause) {
      setConversations((items) =>
        items.map((conversation) =>
          conversation.id === conversationId
            ? {
                ...conversation,
                commitments: conversation.commitments.map((item) =>
                  item.id === commitment.id
                    ? { ...item, status: commitment.status, reviewStatus: commitment.reviewStatus }
                    : item,
                ),
              }
            : conversation,
        ),
      );
      notify("danger", `No se pudo actualizar el compromiso: ${errorMessage(cause)}`);
    }
  }

  async function reviewCommitment(
    conversationId: string,
    commitment: Commitment,
    reviewStatus: "confirmed" | "discarded",
  ) {
    const previous = commitment.reviewStatus;
    const previousItems = conversationsRef.current;
    setConversations((items) => items.map((conversation) => conversation.id === conversationId
      ? {
          ...conversation,
          commitments: reviewStatus === "discarded"
            ? conversation.commitments.filter((item) => item.id !== commitment.id)
            : conversation.commitments.map((item) => item.id === commitment.id ? { ...item, reviewStatus } : item),
        }
      : conversation));
    if (!session) return;
    try {
      await updateRemoteCommitment(commitment.id, { reviewStatus }, session);
      notify("success", reviewStatus === "confirmed" ? "Compromiso sugerido confirmado." : "Sugerencia descartada.");
    } catch (cause) {
      setConversations(previousItems);
      notify("danger", `No se pudo revisar el compromiso: ${errorMessage(cause)}`);
    }
  }

  async function requestAnalysis(conversation: Conversation) {
    if (!session || analyzingConversationId) return;
    setAnalyzingConversationId(conversation.id);
    setConversations((items) => items.map((item) => item.id === conversation.id
      ? { ...item, analysisStatus: "queued", analysisError: undefined }
      : item));
    try {
      await requestRemoteConversationAnalysis(conversation.id, session);
      notify("success", "Análisis solicitado. El resultado aparecerá automáticamente.");
      window.setTimeout(() => void refreshRemoteWorkspace(session, () => true), 8_000);
    } catch (cause) {
      setConversations((items) => items.map((item) => item.id === conversation.id
        ? { ...item, analysisStatus: "error", analysisError: errorMessage(cause) }
        : item));
      notify("danger", `No se pudo iniciar el análisis: ${errorMessage(cause)}`);
    } finally {
      setAnalyzingConversationId("");
    }
  }

  async function addCommitment(
    conversation: Conversation,
    input: Omit<Commitment, "id" | "conversationId" | "createdAt">,
  ) {
    try {
      const saved: Commitment = session
        ? await createRemoteCommitment(conversation.id, input, session)
        : { ...input, id: `commitment-${Date.now().toString(36)}`, conversationId: conversation.id };
      setConversations((items) => items.map((item) => item.id === conversation.id
        ? { ...item, commitments: [...item.commitments, saved] }
        : item));
      notify("success", "Compromiso creado y agregado al seguimiento.");
    } catch (cause) {
      notify("danger", `No se pudo crear el compromiso: ${errorMessage(cause)}`);
      throw cause;
    }
  }

  async function updateConversationOperations(
    conversation: Conversation,
    changes: Partial<Pick<Conversation, "status" | "priority" | "assignedTo" | "nextAction">>,
  ) {
    const previous = {
      status: conversation.status,
      priority: conversation.priority,
      assignedTo: conversation.assignedTo,
      nextAction: conversation.nextAction,
    };
    setConversations((items) => items.map((item) => item.id === conversation.id ? { ...item, ...changes } : item));
    if (!session) {
      notify("success", "Seguimiento operativo actualizado.");
      return;
    }
    try {
      await updateRemoteConversation(conversation.id, changes, session);
      notify("success", "Seguimiento operativo actualizado.");
    } catch (cause) {
      setConversations((items) => items.map((item) => item.id === conversation.id ? { ...item, ...previous } : item));
      notify("danger", `No se pudo actualizar el seguimiento: ${errorMessage(cause)}`);
      throw cause;
    }
  }

  async function markRead(conversation: Conversation) {
    setConversations((items) => items.map((item) => (item.id === conversation.id ? { ...item, unreadCount: 0 } : item)));
    if (!session) return;
    try {
      await updateRemoteConversation(conversation.id, { unreadCount: 0 }, session);
    } catch (cause) {
      notify("warning", `No se pudo sincronizar el cambio: ${errorMessage(cause)}`);
    }
  }

  async function toggleConversationManagement(conversation: Conversation, enabled: boolean) {
    const previous = conversation.managedFromApp;
    setConversations((items) => items.map((item) => item.id === conversation.id ? { ...item, managedFromApp: enabled } : item));
    if (!session) return;
    try {
      await updateRemoteConversation(conversation.id, { managedFromApp: enabled }, session);
      notify("success", enabled ? "Conversación habilitada para responder desde Conversa Smart." : "Conversación cambiada a sólo observación.");
    } catch (cause) {
      setConversations((items) => items.map((item) => item.id === conversation.id ? { ...item, managedFromApp: previous } : item));
      notify("danger", `No se pudo cambiar el modo de gestión: ${errorMessage(cause)}`);
    }
  }

  async function sendWhatsAppReply(conversation: Conversation, body: string) {
    if (!session) return;
    setSendingConversationId(conversation.id);
    try {
      const message = await sendRemoteWhatsAppText(conversation.id, body, session);
      setConversations((items) => items.map((item) => item.id === conversation.id ? {
        ...item,
        messages: [...item.messages, message],
        lastMessage: message.body,
        lastMessageAt: message.sentAtIso ?? message.sentAt,
        updatedAt: message.sentAtIso ?? message.sentAt,
      } : item));
      notify("success", "Respuesta enviada mediante WhatsApp Business.");
    } catch (cause) {
      notify("danger", `No se pudo enviar la respuesta: ${errorMessage(cause)}`);
      throw cause;
    } finally {
      setSendingConversationId("");
    }
  }

  async function openMessageMedia(conversation: Conversation, message: Message) {
    if (!session || message.mediaStatus !== "stored") {
      notify("warning", "La evidencia no está disponible en esta sesión.");
      return;
    }
    const popup = window.open("about:blank", "_blank");
    if (popup) {
      popup.opener = null;
      popup.document.title = "Preparando evidencia";
      popup.document.body.textContent = "Generando acceso temporal seguro…";
    }
    setOpeningMediaId(message.id);
    try {
      const media = await getRemoteMessageMedia(conversation.id, message.id, session, message.sentAtIso ?? message.sentAt);
      if (popup) popup.location.replace(media.downloadUrl);
      else {
        const link = document.createElement("a");
        link.href = media.downloadUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.click();
      }
    } catch (cause) {
      popup?.close();
      notify("danger", `No se pudo abrir la evidencia: ${errorMessage(cause)}`);
    } finally {
      setOpeningMediaId("");
    }
  }

  async function resolveMessageMedia(conversation: Conversation, message: Message): Promise<MediaDownload> {
    if (!session || message.mediaStatus !== "stored") throw new Error("MEDIA_NOT_AVAILABLE");
    return getRemoteMessageMedia(conversation.id, message.id, session, message.sentAtIso ?? message.sentAt);
  }

  async function transcribeAudioMessage(conversation: Conversation, message: Message) {
    if (!session) {
      notify("warning", "Inicia sesión para transcribir audios almacenados en AWS.");
      return;
    }
    setTranscribingMessageId(message.id);
    try {
      const result = await startRemoteAudioTranscription(conversation.id, message.id, session);
      setConversations((items) => items.map((item) => item.id === conversation.id ? {
        ...item,
        messages: item.messages.map((candidate) => candidate.id === message.id ? {
          ...candidate,
          ...result.item,
        } : candidate),
      } : item));
      notify(
        "success",
        result.alreadyCompleted
          ? "La transcripción ya estaba disponible."
          : result.alreadyRunning
            ? "La transcripción continúa en proceso."
            : "Audio enviado a transcripción. Se actualizará automáticamente.",
      );
      if (!result.alreadyCompleted) void pollAudioTranscription(conversation.id, message.id, session);
    } catch (cause) {
      notify("danger", `No se pudo iniciar la transcripción: ${errorMessage(cause)}`);
    } finally {
      setTranscribingMessageId("");
    }
  }

  async function reviewMediaMessage(conversation: Conversation, message: Message) {
    if (!session) {
      notify("warning", "Inicia sesión para revisar evidencia multimedia.");
      return;
    }
    setReviewingMediaId(message.id);
    try {
      const result = await startRemoteMediaReview(conversation.id, message.id, session);
      setConversations((items) => items.map((item) => item.id === conversation.id ? {
        ...item,
        messages: item.messages.map((candidate) => candidate.id === message.id ? {
          ...candidate,
          ...result.item,
        } : candidate),
      } : item));
      notify(
        "success",
        result.alreadyCompleted
          ? "La revisión de esta evidencia ya estaba disponible."
          : result.alreadyRunning
            ? "La revisión de esta evidencia continúa en proceso."
            : "Evidencia enviada a revisión. Se actualizará automáticamente.",
      );
      if (!result.alreadyCompleted) void pollMediaReview(conversation.id, message.id, session);
    } catch (cause) {
      notify("danger", `No se pudo iniciar la revisión: ${errorMessage(cause)}`);
    } finally {
      setReviewingMediaId("");
    }
  }

  async function pollMediaReview(conversationId: string, messageId: string, activeSession: AuthSession) {
    for (const waitMs of [8_000, 20_000, 40_000, 80_000]) {
      await new Promise((resolve) => window.setTimeout(resolve, waitMs));
      try {
        const refreshed = await getRemoteConversation(conversationId, activeSession);
        setConversations((items) => items.map((item) => item.id === conversationId ? refreshed : item));
        const message = refreshed.messages.find((candidate) => candidate.id === messageId);
        if (message?.mediaReviewStatus === "completed") {
          notify("success", "Revisión de evidencia completada.");
          return;
        }
        if (message?.mediaReviewStatus === "failed") {
          notify("warning", `La revisión falló: ${message.mediaReviewError ? errorMessage(new Error(message.mediaReviewError)) : "revisa el formato del archivo"}.`);
          return;
        }
      } catch {
        return;
      }
    }
  }

  async function pollAudioTranscription(conversationId: string, messageId: string, activeSession: AuthSession) {
    for (const waitMs of [8_000, 20_000, 40_000, 80_000]) {
      await new Promise((resolve) => window.setTimeout(resolve, waitMs));
      try {
        const refreshed = await getRemoteConversation(conversationId, activeSession);
        setConversations((items) => items.map((item) => item.id === conversationId ? refreshed : item));
        const message = refreshed.messages.find((candidate) => candidate.id === messageId);
        if (message?.transcriptionStatus === "completed") {
          notify("success", "Transcripción de audio completada.");
          return;
        }
        if (message?.transcriptionStatus === "failed") {
          notify("warning", `La transcripción falló: ${message.transcriptionError ? errorMessage(new Error(message.transcriptionError)) : "revisa el formato del audio"}.`);
          return;
        }
      } catch {
        return;
      }
    }
  }

  async function addInternalNote(conversation: Conversation, note: string) {
    try {
      const message: Message = session
        ? await saveInternalNote(conversation.id, note, session)
        : {
            id: `note-${Date.now().toString(36)}`,
            direction: "outbound",
            sender: "Equipo interno",
            body: `Nota interna: ${note}`,
            sentAt: new Intl.DateTimeFormat("es-CL", { dateStyle: "short", timeStyle: "short" }).format(new Date()),
            sentAtIso: new Date().toISOString(),
            type: "system",
          };
      setConversations((items) =>
        items.map((item) =>
          item.id === conversation.id
            ? { ...item, messages: [...item.messages, message], updatedAt: new Date().toISOString() }
            : item,
        ),
      );
      notify("success", session ? "Nota interna guardada en AWS y registrada en auditoría." : "Nota guardada en este navegador.");
    } catch (cause) {
      notify("danger", `No se pudo guardar la nota: ${errorMessage(cause)}`);
    }
  }

  async function createVisit(
    conversation: Conversation,
    input: Pick<Visit, "scheduledAt" | "assignedTo" | "objective">,
  ) {
    const base = createVisitFromConversation(conversation);
    const draft: Visit = {
      ...base,
      ...input,
      status: input.scheduledAt ? "scheduled" : "draft",
      assignedTo: input.assignedTo.trim(),
      objective: input.objective.trim(),
    };
    try {
      const saved = session ? await createRemoteVisit(conversation.id, draft, session) : draft;
      setVisits((items) => [saved, ...items.filter((item) => item.id !== saved.id)]);
      setConversations((items) => items.map((item) => item.id === conversation.id
        ? { ...item, status: saved.status === "scheduled" ? "scheduled" : item.status, visitRequired: true }
        : item));
      notify("success", saved.status === "scheduled" ? "Visita agendada y vinculada a la conversación." : "Visita creada para coordinar.");
      setView("visits");
    } catch (cause) {
      notify("danger", `No se pudo crear la visita: ${errorMessage(cause)}`);
      throw cause;
    }
  }

  async function createReport(conversation: Conversation) {
    const draft = createReportFromConversation(conversation);
    try {
      const saved = session ? await createRemoteReport(conversation.id, draft, session) : draft;
      setReports((items) => [saved, ...items.filter((item) => item.id !== saved.id)]);
      notify("success", "Borrador de reporte generado con sus mensajes de respaldo.");
      setView("reports");
    } catch (cause) {
      notify("danger", `No se pudo generar el reporte: ${errorMessage(cause)}`);
    }
  }

  async function authenticated(nextSession: AuthSession) {
    setBooting(true);
    try {
      await connectSession(nextSession);
      notify("success", "Espacio de trabajo AWS conectado.");
    } catch (cause) {
      notify("danger", "No se pudo abrir la cuenta: " + errorMessage(cause));
    } finally {
      setBooting(false);
    }
  }

  async function switchOrganization(organizationId: string) {
    if (!session) return;
    setNewWhatsAppOpen(false);
    setBooting(true);
    try {
      const scopedSession = withActiveOrganization(session, organizationId);
      await connectSession(scopedSession, true, organizationId);
      setSelectedId("");
      notify("success", "Organización activa actualizada.");
    } catch (cause) {
      notify("danger", "No se pudo cambiar de organización: " + errorMessage(cause));
    } finally {
      setBooting(false);
    }
  }

  async function refreshUserContext(preferredOrganizationId?: string) {
    if (!session) return;
    const context = await getRemoteUserContext(session, preferredOrganizationId);
    setUserContext(context);
  }

  function logout() {
    setNewWhatsAppOpen(false);
    clearAuthSession();
    setSession(null);
    setUserContext(null);
    hydrateLocal();
    setView("home");
    notify("success", "Sesión cerrada. Volviste al modo demostración.");
  }

  function navigate(next: WorkspaceView) {
    setView(next);
    setMobileMenuOpen(false);
  }

  function notify(tone: "success" | "warning" | "danger", text: string) {
    setToast({ tone, text });
  }

  const activeOrganization = userContext?.organizations.find((item) => item.id === userContext.activeOrganizationId);
  const isAdministrator = activeOrganization?.role === "Administrador";
  const canControlConversations = Boolean(activeOrganization && ["Administrador", "Gerencia", "Operaciones", "Supervisor"].includes(activeOrganization.role));
  const canReplyFromApp = Boolean(activeOrganization && activeOrganization.role !== "Auditor");
  const canEditOperations = Boolean(activeOrganization && activeOrganization.role !== "Auditor");
  const canEditCustomers = Boolean(activeOrganization && ["Administrador", "Gerencia", "Operaciones"].includes(activeOrganization.role));
  const canReviewVsmart = Boolean(activeOrganization && ["Administrador", "Gerencia", "Operaciones", "Supervisor"].includes(activeOrganization.role));
  const canReviewPgrMedia = Boolean(activeOrganization?.id === "pgr-seguridad" && canEditOperations);
  const canStartPaseFirmeWhatsApp = Boolean(
    session
    && activeOrganization
    && activeOrganization.id.toLocaleLowerCase("es-CL").replaceAll("-", "").includes("pasefirme")
    && canReplyFromApp
  );
  const vsmartOrganization = Boolean(activeOrganization && ["pgr-seguridad", "vsmart", "v-smart"].includes(activeOrganization.id));

  const navigation: Array<{ view: WorkspaceView; label: string; icon: IconName; count?: number }> = [
    { view: "home", label: "Inicio", icon: "home" },
    { view: "pilot", label: "Centro de piloto", icon: "sparkles" },
    { view: "conversations", label: "Conversaciones", icon: "chat", count: unreadConversationCount },
    { view: "commitments", label: "Compromisos", icon: "check", count: pendingCommitments },
    { view: "visits", label: "Visitas", icon: "visit", count: visits.filter((item) => item.status === "scheduled").length },
    { view: "reports", label: "Reportes", icon: "document", count: reports.filter((item) => item.status === "draft").length },
    { view: "import", label: "Importar chat", icon: "import" },
    ...(session ? [{ view: "customers" as const, label: "Clientes", icon: "team" as const }] : []),
    ...(session ? [{ view: "organizations" as const, label: "Organizaciones", icon: "settings" as const }] : []),
    ...(isAdministrator ? [
      { view: "channels" as const, label: "Canales", icon: "whatsapp" as const },
      { view: "users" as const, label: "Usuarios", icon: "team" as const },
    ] : []),
  ];
  const title = view === "home" && activeOrganization
    ? { ...VIEW_TITLE[view], kicker: activeOrganization.name.toUpperCase() }
    : VIEW_TITLE[view];
  const syncLabel = !session
    ? "DEMO LOCAL"
    : remoteSync.status === "syncing"
      ? "ACTUALIZANDO AWS"
      : remoteSync.status === "error"
        ? remoteSync.lastSuccessAt ? "AWS CON DEMORA" : "AWS SIN CONEXIÓN"
        : "AWS CONECTADO";

  return (
    <div className="friendly-app-shell">
      <aside className={`friendly-sidebar ${mobileMenuOpen ? "open" : ""}`}>
        <div className="friendly-brand">
          <span className="friendly-brand-mark"><Icon name="chat" size={22} /></span>
          <div><strong>Conversa Smart</strong><small>Claridad desde cada mensaje</small></div>
          <button className="mobile-close" onClick={() => setMobileMenuOpen(false)} type="button" aria-label="Cerrar menú"><Icon name="x" size={19} /></button>
        </div>

        <div className="workspace-card organization-switch-card">
          <span>{initials(activeOrganization?.name ?? "Demo")}</span>
          <label>
            <small>Organización activa</small>
            {session && userContext?.organizations.length ? (
              <select aria-label="Cambiar organización" value={activeOrganization?.id ?? ""} onChange={(event) => void switchOrganization(event.target.value)}>
                {userContext.organizations.filter((item) => item.status !== "suspended").map((organization) => <option key={organization.id} value={organization.id}>{organization.name}</option>)}
              </select>
            ) : <strong>Modo demostración</strong>}
          </label>
          <Icon name="chevron" size={16} />
        </div>

        <nav className="friendly-nav" aria-label="Navegación principal">
          <p>TRABAJO DIARIO</p>
          {navigation.map((item) => (
            <button className={view === item.view ? "active" : ""} key={item.view} onClick={() => navigate(item.view)} type="button">
              <Icon name={item.icon} size={19} /><span>{item.label}</span>{item.count ? <b>{item.count}</b> : null}
            </button>
          ))}
        </nav>

        <section className="channel-status-card">
          <div className="channel-status-heading"><span><Icon name="whatsapp" size={18} /></span><div><strong>Canal en tiempo real</strong><small>WhatsApp Business Cloud API</small></div></div>
          <div className="channel-progress"><span style={{ width: "72%" }} /></div>
          <div className="channel-status-footer"><span><i /> Enrutamiento aislado</span><b>Multi-número</b></div>
        </section>

        <div className="sidebar-flex" />

        <section className="privacy-note"><Icon name="check" size={17} /><div><strong>Privacidad por diseño</strong><span>Roles, auditoría y separación por empresa.</span></div></section>

        <footer className="friendly-sidebar-user">
          <span className="sidebar-user-avatar">{session ? initials(session.email) : "DE"}</span>
          <div><strong>{session?.email ?? "Modo demostración"}</strong><small>{activeOrganization ? activeOrganization.role + " · " + activeOrganization.name : "Datos locales + ejemplos"}</small></div>
          {session ? <button onClick={logout} type="button" title="Cerrar sesión"><Icon name="external" size={17} /></button> : <button onClick={() => setAuthOpen(true)} type="button" title="Ingresar"><Icon name="arrow" size={17} /></button>}
        </footer>
      </aside>

      {mobileMenuOpen ? <button className="mobile-overlay" aria-label="Cerrar menú" onClick={() => setMobileMenuOpen(false)} type="button" /> : null}

      <div className="friendly-main">
        <header className="friendly-topbar">
          <div className="topbar-left"><button className="mobile-menu-button" onClick={() => setMobileMenuOpen(true)} type="button"><Icon name="menu" size={21} /></button><div><p>{title.kicker}</p><h2>{title.title}</h2></div></div>
          <div className="topbar-right">
            {session ? (
              <button
                className="button secondary compact"
                disabled={remoteSync.status === "syncing"}
                onClick={() => void manualRefresh()}
                title={remoteSync.error || (remoteSync.lastSuccessAt
                  ? `Última sincronización: ${formatSyncTime(remoteSync.lastSuccessAt)}`
                  : "Actualizar conversaciones")}
                type="button"
              >
                <Icon name="clock" size={15} />
                {remoteSync.status === "syncing" ? "Actualizando…" : "Actualizar"}
              </button>
            ) : null}
            <span
              className={`mode-chip ${session ? (remoteSync.status === "error" ? "demo" : "connected") : "demo"}`}
              title={remoteSync.error || (remoteSync.lastSuccessAt
                ? `Última sincronización: ${formatSyncTime(remoteSync.lastSuccessAt)}`
                : undefined)}
            ><i /> {syncLabel}</span>
            <button className="topbar-icon" onClick={() => navigate("conversations")} type="button" aria-label="Abrir conversaciones sin leer"><Icon name="bell" size={19} />{unreadConversationCount ? <b>{unreadConversationCount}</b> : null}</button>
            {session ? <button className="topbar-user" onClick={() => navigate(isAdministrator ? "users" : "organizations")} type="button"><span>{initials(session.email)}</span><div><strong>{session.email}</strong><small>{activeOrganization ? activeOrganization.role + " · " + activeOrganization.name : "Sin organización"}</small></div><Icon name="chevron" size={15} /></button> : <button className="button secondary compact" onClick={() => setAuthOpen(true)} type="button">Ingresar como equipo</button>}
          </div>
        </header>

        <main className={view === "conversations" ? "main-content conversation-content-page" : "main-content"}>
          {booting ? <BootScreen /> : null}
          {!booting && view === "home" ? <HomeView conversations={conversations} visits={visits} reports={reports} remote={Boolean(session)} onNavigate={navigate} onLogin={() => setAuthOpen(true)} /> : null}
          {!booting && view === "pilot" ? <PilotCenter session={session} organizationName={activeOrganization?.name} isAdministrator={Boolean(isAdministrator)} remote={Boolean(session)} onCreateScenario={handleImport} onNavigate={navigate} notify={notify} /> : null}
          {!booting && view === "conversations" ? <ConversationView conversations={conversations} selectedId={selectedId} onSelect={(id) => void selectConversation(id)} onToggleCommitment={(conversationId, commitment) => void toggleCommitment(conversationId, commitment)} onReviewCommitment={(conversationId, commitment, status) => void reviewCommitment(conversationId, commitment, status)} onRequestAnalysis={(conversation) => void requestAnalysis(conversation)} analyzingConversationId={analyzingConversationId} onCreateCommitment={(conversation, input) => addCommitment(conversation, input)} onUpdateOperations={(conversation, changes) => updateConversationOperations(conversation, changes)} onCreateVisit={(conversation, input) => createVisit(conversation, input)} onCreateReport={(conversation) => void createReport(conversation)} onMarkRead={(conversation) => void markRead(conversation)} onAddInternalNote={(conversation, note) => void addInternalNote(conversation, note)} onToggleManagement={(conversation, enabled) => void toggleConversationManagement(conversation, enabled)} onSendWhatsApp={(conversation, body) => sendWhatsAppReply(conversation, body)} onResolveMedia={(conversation, message) => resolveMessageMedia(conversation, message)} onOpenMedia={(conversation, message) => openMessageMedia(conversation, message)} openingMediaId={openingMediaId} onReviewMedia={(conversation, message) => reviewMediaMessage(conversation, message)} reviewingMediaId={reviewingMediaId} canReviewMedia={canReviewPgrMedia} onTranscribeAudio={(conversation, message) => transcribeAudioMessage(conversation, message)} transcribingMessageId={transcribingMessageId} canTranscribe={canReviewPgrMedia} canControlManagement={canControlConversations} canEditOperations={canEditOperations} canReply={canReplyFromApp} sendingConversationId={sendingConversationId} onQueryVsmart={handleVsmartQuery} canQueryVsmart={Boolean(session && vsmartOrganization)} canReviewVsmart={canReviewVsmart} canStartNewWhatsApp={canStartPaseFirmeWhatsApp} onStartNewWhatsApp={() => setNewWhatsAppOpen(true)} segmentByCustomer={canStartPaseFirmeWhatsApp} loadingDetail={loadingDetail} /> : null}
          {!booting && view === "commitments" ? <CommitmentsView conversations={conversations} onToggle={(conversationId, commitment) => void toggleCommitment(conversationId, commitment)} onOpenConversation={(id) => void selectConversation(id)} /> : null}
          {!booting && view === "visits" ? <VisitsView visits={visits} conversations={conversations} onOpenConversation={(id) => void selectConversation(id)} /> : null}
          {!booting && view === "reports" ? <ReportsView reports={reports} conversations={conversations} onOpenConversation={(id) => void selectConversation(id)} /> : null}
          {!booting && view === "import" ? <ImportStudio
            onImported={handleImport}
            onHistoryImported={handleHistoryImport}
            existingGroupConversations={conversations.filter((item) =>
              item.source === "whatsapp_group" || item.id.includes("#group#")
            )}
            organizationName={activeOrganization?.name}
            remote={Boolean(session)}
          /> : null}
          {!booting && view === "customers" && session && activeOrganization ? <CustomersView organizationName={activeOrganization.name} session={session} canEdit={canEditCustomers} notify={notify} /> : null}
          {!booting && view === "channels" && session && activeOrganization && isAdministrator ? <ChannelsView organizationName={activeOrganization.name} session={session} notify={notify} /> : null}
          {!booting && view === "organizations" && session && userContext ? <OrganizationsView context={userContext} session={session} onSwitch={switchOrganization} onRefresh={refreshUserContext} notify={notify} /> : null}
          {!booting && view === "users" && session && activeOrganization && isAdministrator ? <UsersView organizationId={activeOrganization.id} organizationName={activeOrganization.name} session={session} notify={notify} /> : null}
        </main>
      </div>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onAuthenticated={(nextSession) => void authenticated(nextSession)} />
      {session && activeOrganization ? <PaseFirmeNewWhatsAppModal open={newWhatsAppOpen} session={session} organizationName={activeOrganization.name} onClose={() => setNewWhatsAppOpen(false)} notify={notify} /> : null}
      {toast ? <div className={`toast ${toast.tone}`}><Icon name={toast.tone === "success" ? "check" : "bell"} size={18} /><span>{toast.text}</span><button onClick={() => setToast(null)} type="button"><Icon name="x" size={15} /></button></div> : null}
    </div>
  );
}

function BootScreen() {
  return <div className="boot-screen"><span className="boot-mark"><Icon name="chat" size={28} /></span><strong>Preparando tu mesa de trabajo</strong><p>Cargando conversaciones, compromisos y reportes…</p><div><i /><i /><i /></div></div>;
}

function formatSyncTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "sin hora";
  return new Intl.DateTimeFormat("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

function initials(value: string) {
  return value.split(/[@.\s_-]+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "CS";
}

function errorMessage(cause: unknown) {
  const raw = cause instanceof Error ? cause.message : "ERROR_DESCONOCIDO";
  const [code, providerCode] = raw.split("::");
  const labels: Record<string, string> = {
    META_ACCESS_TOKEN_INVALID: "el token de Meta venció o dejó de ser válido; actualízalo en Canales",
    META_MESSAGING_PERMISSION_REQUIRED: "el token de Meta no tiene permiso para enviar mensajes de WhatsApp",
    WHATSAPP_RECIPIENT_IS_SENDER: "el destinatario coincide con el número empresarial",
    WHATSAPP_MESSAGE_UNDELIVERABLE: "Meta no pudo entregar el mensaje a ese número",
    WHATSAPP_RECIPIENT_NOT_ALLOWED: "el destinatario no está habilitado para recibir mensajes de esta cuenta",
    WHATSAPP_ACCOUNT_LOCKED: "Meta bloqueó temporalmente la cuenta de WhatsApp Business",
    WHATSAPP_PAYMENT_REQUIRED: "la cuenta de WhatsApp Business requiere completar su método de pago",
    WHATSAPP_TEMPLATE_REQUIRED: "la ventana de atención terminó y Meta exige una plantilla aprobada",
    WHATSAPP_NUMBER_NOT_REGISTERED: "el número todavía no está registrado para enviar mediante Cloud API",
    TRANSCRIPTION_AUDIO_REQUIRED: "el mensaje seleccionado no es un audio",
    TRANSCRIPTION_MEDIA_NOT_STORED: "el audio todavía no está almacenado en AWS",
    TRANSCRIPTION_MEDIA_FORMAT_UNSUPPORTED: "el formato del audio no es compatible con la transcripción",
    OPENAI_TRANSCRIPTION_NOT_CONFIGURED: "falta conectar una clave de OpenAI API en el Conector local de esta organización",
    OPENAI_PGR_ONLY: "OpenAI está habilitado sólo para PGR Seguridad",
    OPENAI_OPERATIONAL_GROUP_ONLY: "OpenAI está habilitado sólo para grupos operacionales de PGR",
    MEDIA_REVIEW_OPERATIONAL_GROUP_ONLY: "la revisión multimedia está disponible sólo en grupos operacionales de PGR",
    MEDIA_REVIEW_TYPE_REQUIRED: "selecciona una foto, video o documento",
    MEDIA_REVIEW_NOT_STORED: "el archivo todavía no está almacenado en AWS",
    MEDIA_REVIEW_FORMAT_UNSUPPORTED: "el formato del archivo no es compatible con la revisión",
    MEDIA_REVIEW_TOO_LARGE: "el archivo supera el límite de revisión",
    MEDIA_REVIEW_REQUEST_FAILED: "OpenAI no pudo revisar esta evidencia",
    MEDIA_LOOKUP_FAILED: "no se pudo localizar el archivo almacenado; intenta actualizar nuevamente",
    MEDIA_LINK_FAILED: "no se pudo preparar el acceso temporal a la evidencia",
    MEDIA_ACCESS_FAILED: "no se pudo abrir la evidencia almacenada",
    TRANSCRIPTION_REQUEST_FAILED: "OpenAI no pudo iniciar o completar la transcripción",
  };
  if (labels[code]) return labels[code];
  if (code === "META_SEND_FAILED" && providerCode) {
    return `Meta rechazó el envío (código ${providerCode})`;
  }
  return code.replaceAll("_", " ").toLocaleLowerCase("es-CL");
}
